/* ──────────────────────────────────────────────────────────────
 *  ExploreTable — MUI DataGrid with dynamic columns per bushing type
 *
 *  Supports sorting by header click, row selection that
 *  propagates to the global sample store.
 *  A radio-button column precedes the Sample ID column.
 *  The best sample's ID is marked with a ★ suffix.
 * ────────────────────────────────────────────────────────────── */

import React, { useMemo } from 'react';
import Box from '@mui/material/Box';
import Radio from '@mui/material/Radio';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import {
  DataGrid,
  type GridColDef,
  type GridRenderCellParams,
  type GridRowSelectionModel,
} from '@mui/x-data-grid';
import type { BushingType } from '@/types/bushing';
import type { SampleRow } from '@/types/sample';
import { getExploreColumns, toGridColDefs } from './exploreColumns';

/* ─── Types ─────────────────────────────────────────────────── */

interface ExploreTableProps {
  bushingType: BushingType;
  rows: SampleRow[];
  selectedId: string | null;
  bestSampleId: string | null;
  onRowSelect: (id: string | null) => void;
  loading?: boolean;
  message?: string;
}

/* ─── Constants ─────────────────────────────────────────────── */


const RADIO_COL_WIDTH = 30;

const dataGridSx = {
  fontSize: '15px',

  '& .MuiDataGrid-columnHeader': {
    fontSize: '12px',
    fontWeight: 700,
    alignItems: 'center',
  },
  '& .MuiDataGrid-columnHeaderTitleContainer': {
    display: 'flex',
    alignItems: 'center',
    flexDirection: 'row-reverse',
    justifyContent: 'flex-end',
    width: '100%',
    gap: 0.25,
  },
  '& .MuiDataGrid-columnHeaderTitleContainerContent': {
    order: 2,
    overflow: 'visible',
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
  },
  '& .MuiDataGrid-iconButtonContainer, & .MuiDataGrid-menuIcon': {
    order: 1,
    visibility: 'visible !important',
    width: 'auto !important',
    opacity: 1,
    margin: 0,
  },
  '& .MuiDataGrid-columnHeaderTitle': {
    whiteSpace: 'normal',
    overflow: 'visible',
    textOverflow: 'clip',
    lineHeight: 1.25,
    fontSize: '12px',
    fontWeight: 700,
    textAlign: 'center',
    width: '100%',
  },
  '& .MuiDataGrid-cell': {
    fontSize: '15px',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
  },
  '& .MuiDataGrid-sortIcon': {
    visibility: 'visible !important',
    opacity: '1 !important',
  },
  '& .MuiDataGrid-columnHeader:not(.MuiDataGrid-columnHeader--sorted) .MuiDataGrid-sortIcon': {
    opacity: 1,
  },
  '& .MuiDataGrid-columnHeader:hover .MuiDataGrid-menuIcon': {
    visibility: 'visible',
  },
  '& .MuiDataGrid-row.Mui-selected': {
    backgroundColor: 'rgba(255, 127, 14, 0.15)',
  },
  '& .MuiDataGrid-row.Mui-selected:hover': {
    backgroundColor: 'rgba(255, 127, 14, 0.25)',
  },
} as const;

const headerTextSx = {
  width: '100%',
  whiteSpace: 'normal',
  overflow: 'visible',
  textOverflow: 'clip',
  lineHeight: 1.25,
  fontSize: '12px',
  fontWeight: 700,
  textAlign: 'center',
  wordBreak: 'normal',
  overflowWrap: 'break-word',
} as const;

/* ─── Helpers ───────────────────────────────────────────────── */


const enhanceColumnHeader = (col: GridColDef): GridColDef => {
  const name = col.headerName ?? col.field;
  return {
    ...col,
    description: col.description ?? name,
    headerAlign: 'center',
    align: 'center',
    renderHeader: () => (
      <Tooltip title={name} arrow placement="top">
        <Box component="span" sx={headerTextSx}>
          {name}
        </Box>
      </Tooltip>
    ),
  };
};

const createRadioColumn = (
  selectedId: string | null,
  onRowSelect: (id: string | null) => void,
): GridColDef => ({
  field: '__radio__',
  headerName: '',
  width: RADIO_COL_WIDTH,
  minWidth: RADIO_COL_WIDTH,
  maxWidth: RADIO_COL_WIDTH,
  sortable: false,
  disableColumnMenu: true,
  resizable: false,
  headerAlign: 'center',
  align: 'center',
  renderCell: (params: GridRenderCellParams) => (
    <Radio
      size="small"
      checked={String(params.row.id) === selectedId}
      onChange={() => onRowSelect(String(params.row.id))}
      onClick={(e) => e.stopPropagation()}
      sx={{ p: 0 }}
    />
  ),
});

const decorateIdColumn = (col: GridColDef, bestSampleId: string | null): GridColDef => {
  if (col.field !== 'id') return col;
  return {
    ...col,
    align: 'center',
    headerAlign: 'center',
    renderCell: (params: GridRenderCellParams) => {
      const id = String(params.value ?? '');
      const isBest = id === bestSampleId;
      return (
        <Box
          component="span"
          sx={{ fontWeight: isBest ? 700 : 400, fontSize: '15px', width: '100%', textAlign: 'center' }}
        >
          {isBest ? `${id} ★` : id}
        </Box>
      );
    },
  };
};

/* ─── Component ─────────────────────────────────────────────── */

const ExploreTable: React.FC<ExploreTableProps> = ({
  bushingType,
  rows,
  selectedId,
  bestSampleId,
  onRowSelect,
  loading = false,
  message,
}) => {
  const columnDefs = useMemo<GridColDef[]>(() => {
    const baseCols = toGridColDefs(getExploreColumns(bushingType))
      .map(enhanceColumnHeader)
      .map((col) => decorateIdColumn(col, bestSampleId));

    return [createRadioColumn(selectedId, onRowSelect), ...baseCols];
  }, [bushingType, bestSampleId, selectedId, onRowSelect]);

  const rowSelection = useMemo<GridRowSelectionModel>(
    () => (selectedId ? [selectedId] : []),
    [selectedId],
  );

  const handleSelectionChange = (model: GridRowSelectionModel) => {
    onRowSelect(model.length > 0 ? String(model[0]) : null);
  };

  if (message) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}>
        <Typography color="text.secondary" fontSize={16}>
          {message}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ height: 450, width: '100%' }}>
      <DataGrid
        rows={rows}
        columns={columnDefs}
        getRowId={(row) => String(row.id)}
        loading={loading}
        rowSelectionModel={rowSelection}
        onRowSelectionModelChange={handleSelectionChange}
        pageSizeOptions={[25, 50, 100]}
        initialState={{ pagination: { paginationModel: { pageSize: 100 } } }}
        density="compact"
        disableMultipleRowSelection
        disableRowSelectionOnClick
        columnHeaderHeight={80}
        sx={dataGridSx}
      />
    </Box>
  );
};

export default React.memo(ExploreTable);